import { useEffect, useState, useMemo } from "react";
import {
  FlaskConical, ArrowRight, TrendingUp, TrendingDown,
  Clock, AlertOctagon, Activity, Calendar as CalendarIcon,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "./ui/select";
import { Card } from "./ui/card";
import { toast } from "sonner";
import api from "../lib/api";

const money = (n) => `₡${Number(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const SCENARIOS = [
  { value: "no_show", label: "No se presenta", icon: AlertOctagon, color: "text-red-600" },
  { value: "partial", label: "Sale temprano / llega tarde", icon: Clock, color: "text-amber-600" },
  { value: "overtime", label: "Hace horas extras", icon: TrendingUp, color: "text-emerald-600" },
  { value: "incident", label: "Registra incidencia", icon: Activity, color: "text-blue-600" },
];

const INCIDENT_OPTIONS = [
  { value: "vacaciones", label: "Vacaciones" },
  { value: "permiso_con_goce", label: "Permiso con goce" },
  { value: "permiso_sin_goce", label: "Permiso sin goce" },
  { value: "incapacidad_ccss", label: "Incapacidad CCSS" },
  { value: "incapacidad_ins", label: "Incapacidad INS" },
  { value: "ausencia_injustificada", label: "Ausencia injustificada" },
];

export default function PayrollSimulator({ children }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState([]);
  const [loadingDays, setLoadingDays] = useState(false);
  const [selectedKey, setSelectedKey] = useState("");   // `${period_id}__${employee_id}__${date}`
  const [scenario, setScenario] = useState("no_show");
  const [actualHours, setActualHours] = useState(4);
  const [overtimeHours, setOvertimeHours] = useState(2);
  const [incidentType, setIncidentType] = useState("vacaciones");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const loadDays = async () => {
    setLoadingDays(true);
    try {
      const { data } = await api.get("/payroll/projected-days");
      // Only show future or today's projected days (those are the ones that can be simulated)
      const future = (Array.isArray(data) ? data : []).filter((d) => d.in_the_future);
      setDays(future.slice(0, 200));   // soft cap
    } catch {
      toast.error("No se pudieron cargar los días proyectados");
    } finally { setLoadingDays(false); }
  };

  useEffect(() => {
    if (open) {
      loadDays();
      setResult(null);
      setSelectedKey("");
    }
  }, [open]);

  const selectedDay = useMemo(
    () => days.find((d) => `${d.period_id}__${d.employee_id}__${d.target_date}` === selectedKey),
    [days, selectedKey],
  );

  const handleRun = async () => {
    if (!selectedDay) { toast.error("Seleccione un día proyectado"); return; }
    setRunning(true);
    try {
      const body = {
        employee_id: selectedDay.employee_id,
        period_id: selectedDay.period_id,
        target_date: selectedDay.target_date,
        scenario,
      };
      if (scenario === "partial") body.actual_hours = Number(actualHours);
      if (scenario === "overtime") body.overtime_hours = Number(overtimeHours);
      if (scenario === "incident") body.incident_type = incidentType;
      const { data } = await api.post("/payroll/simulate", body);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al simular");
    } finally { setRunning(false); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {children || (
          <Button variant="outline" size="sm" data-testid="payroll-simulator-trigger">
            <FlaskConical className="w-3.5 h-3.5 mr-2" />
            Simulador de impacto
          </Button>
        )}
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto"
        data-testid="payroll-simulator-sheet"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-blue-600" />
            Simulador de impacto
          </SheetTitle>
          <SheetDescription>
            ¿Qué pasa si un empleado no se presenta, trabaja menos horas o hace horas extras?
            Simulá el resultado antes de cerrar la planilla. <strong>No modifica nada.</strong>
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 py-5">
          {/* Day selector */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              Día proyectado
            </Label>
            <Select value={selectedKey} onValueChange={setSelectedKey} disabled={loadingDays || days.length === 0}>
              <SelectTrigger className="mt-1.5" data-testid="simulator-day-select">
                <SelectValue placeholder={loadingDays ? "Cargando…" : (days.length === 0 ? "No hay días proyectados futuros" : "Seleccione un día y empleado")} />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {days.map((d) => {
                  const k = `${d.period_id}__${d.employee_id}__${d.target_date}`;
                  return (
                    <SelectItem key={k} value={k}>
                      <span className="font-mono text-xs text-zinc-500">{d.target_date.slice(5)}</span>
                      <span className="mx-1">·</span>
                      <span className="font-medium">{d.employee_name || d.employee_code}</span>
                      <span className="ml-1 text-xs text-zinc-400">({d.projected_hours}h · {money(d.projected_pay)})</span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {selectedDay && (
              <p className="text-xs text-zinc-500 mt-1.5 flex items-center gap-1.5">
                <CalendarIcon className="w-3 h-3" />
                Proyectado: <strong className="text-zinc-700">{selectedDay.projected_hours}h</strong> ·
                <strong className="text-zinc-700">{money(selectedDay.projected_pay)}</strong> ·
                período <span className="font-mono">{selectedDay.period_label}</span>
              </p>
            )}
          </div>

          {/* Scenario */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              Escenario hipotético
            </Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {SCENARIOS.map((s) => {
                const Icon = s.icon;
                const active = scenario === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setScenario(s.value)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors
                      ${active
                        ? "border-zinc-900 bg-zinc-50 ring-1 ring-zinc-900"
                        : "border-zinc-200 hover:border-zinc-400"}`}
                    data-testid={`simulator-scenario-${s.value}`}
                  >
                    <Icon className={`w-4 h-4 ${s.color} shrink-0`} strokeWidth={1.7} />
                    <span className="font-medium text-zinc-800">{s.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Scenario-specific input */}
          {scenario === "partial" && (
            <div>
              <Label className="text-xs">Horas que realmente trabajará</Label>
              <Input
                type="number" step="0.25" min="0" max="12"
                value={actualHours}
                onChange={(e) => setActualHours(e.target.value)}
                data-testid="simulator-actual-hours"
              />
            </div>
          )}
          {scenario === "overtime" && (
            <div>
              <Label className="text-xs">Horas extra adicionales</Label>
              <Input
                type="number" step="0.25" min="0" max="8"
                value={overtimeHours}
                onChange={(e) => setOvertimeHours(e.target.value)}
                data-testid="simulator-overtime-hours"
              />
            </div>
          )}
          {scenario === "incident" && (
            <div>
              <Label className="text-xs">Tipo de incidencia</Label>
              <Select value={incidentType} onValueChange={setIncidentType}>
                <SelectTrigger data-testid="simulator-incident-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INCIDENT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Button
            onClick={handleRun}
            disabled={running || !selectedDay}
            className="w-full bg-zinc-900 text-white hover:bg-zinc-800"
            data-testid="simulator-run"
          >
            {running ? "Simulando…" : (
              <span className="flex items-center gap-2">
                Calcular impacto <ArrowRight className="w-4 h-4" />
              </span>
            )}
          </Button>

          {/* Result */}
          {result && (
            <Card className="p-5 bg-gradient-to-br from-zinc-50 to-white border-zinc-200" data-testid="simulator-result">
              <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-1">Resultado</p>
              <p className="text-sm text-zinc-700 mb-4 leading-snug">{result.description}</p>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-zinc-400">Proyectado original</p>
                  <p className="text-lg font-bold text-zinc-900">{money(result.projection.day_pay)}</p>
                  <p className="text-[11px] text-zinc-500">{result.projection.ordinary_hours}h</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-zinc-400">Simulado</p>
                  <p className="text-lg font-bold text-zinc-900">{money(result.simulation.day_pay)}</p>
                  <p className="text-[11px] text-zinc-500">
                    {result.simulation.ordinary_hours}h
                    {result.simulation.overtime_hours > 0 && ` + ${result.simulation.overtime_hours}h extra`}
                  </p>
                </div>
              </div>

              <div className="border-t border-zinc-200 pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-600">Ajuste en la próxima planilla:</span>
                  <span className={`font-mono font-bold text-base ${
                    result.delta.pay < 0 ? "text-red-600" : result.delta.pay > 0 ? "text-emerald-700" : "text-zinc-500"
                  }`} data-testid="simulator-delta-pay">
                    {result.delta.pay >= 0 ? "+" : ""}{money(result.delta.pay)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-600">Neto actual del empleado:</span>
                  <span className="font-mono text-sm text-zinc-700">{money(result.current_period_net)}</span>
                </div>
                <div className="flex items-center justify-between bg-zinc-100 rounded px-2 py-1.5">
                  <span className="text-xs font-semibold text-zinc-700">Neto aproximado en próxima planilla:</span>
                  <span className="font-mono font-bold text-sm text-zinc-900">
                    {money(result.approx_next_period_impact.simulated_net)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-600">Cambio neto aprox.:</span>
                  <span className={`font-mono font-bold text-sm flex items-center gap-1 ${
                    result.approx_next_period_impact.approx_net_change < 0 ? "text-red-600" : "text-emerald-700"
                  }`}>
                    {result.approx_next_period_impact.approx_net_change < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
                    {result.approx_next_period_impact.approx_net_change >= 0 ? "+" : ""}{money(result.approx_next_period_impact.approx_net_change)}
                  </span>
                </div>
              </div>

              <p className="text-[10px] text-zinc-400 mt-3 italic leading-relaxed">
                {result.approx_next_period_impact.note}
              </p>
            </Card>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
